import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, withStyles, IconButton } from '@material-ui/core';
import { useLoadCanvas, useLineAndArrow, useDropUnit } from '../../services/hooks/canvasHooks';
import Actions from './Actions';
import Tools from './Tools';
import styles from './styles';
import { TREATMENT_PHOTO_WIDTH } from '../../constants/treatment';
import { exportCanvasToDataURL } from '../../services/helpers/canvasExport';
import { useSelector } from 'react-redux';
import { useDragLayer } from 'react-dnd';
import { Preview } from 'react-dnd-preview';
import UnitButtonLayer from '../customer/treatment/TreatmentDesignForm/UnitButton/UnitButtonLayer';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

function Canvas({
    classes,
    formRef,
    updateForm,
    canvas,
    photo,
    canvasSizeProps,
    dragSnapShot,
    shape,
    borderColour,
    treatmentType
}) {
    const hasOtherPages = Array.isArray(photo?.otherPagesUrl) && photo.otherPagesUrl.length > 0;
    const canvasRef = useRef(null);
    const [drawerStyles, setDrawerStyles] = useState({});
    const [backgroundUrl, setBackgroundUrl] = useState(null);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);
    const canvasDataPerPage = useRef({});
    const canvasImagePerPage = useRef({});
    const originalDimensions = useRef({ width: TREATMENT_PHOTO_WIDTH, height: 0 });
    const preloadedImages = useRef({});

    if (!formRef.current.useMultiPageCanvas) {
        formRef.current.useMultiPageCanvas = true;
    }

    const isDrawerLocked = useSelector(({ treatment }) => {
        const canvasMetadata = treatment?.canvasMetadata || {};
        return !['inArrowMode', 'inBrushMode', 'inLineMode', 'inShapeMode'].some(key => canvasMetadata[key]);
    });
    const { isDragging } = useDragLayer(monitor => {
        return {
            isDragging: monitor.isDragging()
        };
    });

    useEffect(() => {
        if (drawerStyles.height && canvas.current) {
            canvas.current.setHeight(drawerStyles.height);
        }
    });

    const initializedRef = useRef(false);
    useEffect(() => {
        if (!initializedRef.current) {
            if (formRef.current.canvasDataPerPage && Object.keys(formRef.current.canvasDataPerPage).length > 0) {
                canvasDataPerPage.current = { ...formRef.current.canvasDataPerPage };
            } else if (formRef.current.canvasData) {
                canvasDataPerPage.current = { 0: formRef.current.canvasData };
                formRef.current.canvasDataPerPage = canvasDataPerPage.current;
            } else {
                canvasDataPerPage.current = {};
            }

            if (formRef.current.canvasImagePerPage && Object.keys(formRef.current.canvasImagePerPage).length > 0) {
                canvasImagePerPage.current = { ...formRef.current.canvasImagePerPage };
            } else if (formRef.current.canvasImage) {
                canvasImagePerPage.current = { 0: formRef.current.canvasImage };
                formRef.current.canvasImagePerPage = canvasImagePerPage.current;
            } else {
                canvasImagePerPage.current = {};
            }
            initializedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    const loadCanvasForPage = useCallback(() => {
        if (canvas.current && drawerStyles.height) {
            const pageData = canvasDataPerPage.current[currentPageIndex];

            canvas.current.clear();

            if (pageData) {
                try {
                    const jsonCanvas = JSON.parse(pageData);
                    canvas.current.loadFromJSON(jsonCanvas, () => {
                        canvas.current.renderAll();

                        if (isZoomed && canvas.current) {
                            const availableWidth = window.innerWidth * 0.95;
                            const availableHeight = window.innerHeight * 0.85;
                            const scaleX = availableWidth / originalDimensions.current.width;
                            const scaleY = availableHeight / originalDimensions.current.height;
                            const scale = Math.min(scaleX, scaleY);

                            canvas.current.setZoom(scale);
                            canvas.current.setWidth(originalDimensions.current.width * scale);
                            canvas.current.setHeight(originalDimensions.current.height * scale);
                            canvas.current.renderAll();
                        }
                    });
                } catch (e) {
                    console.error('Error loading canvas data:', e);
                }
            } else if (isZoomed && canvas.current) {
                const availableWidth = window.innerWidth * 0.95;
                const availableHeight = window.innerHeight * 0.85;
                const scaleX = availableWidth / originalDimensions.current.width;
                const scaleY = availableHeight / originalDimensions.current.height;
                const scale = Math.min(scaleX, scaleY);

                canvas.current.setZoom(scale);
                canvas.current.setWidth(originalDimensions.current.width * scale);
                canvas.current.setHeight(originalDimensions.current.height * scale);
                canvas.current.renderAll();
            }
        }
    }, [canvas, currentPageIndex, drawerStyles.height, isZoomed]);

    useEffect(() => {
        loadCanvasForPage();

        if (isZoomed && canvas.current) {
            setTimeout(() => {
                const availableWidth = window.innerWidth * 0.95;
                const availableHeight = window.innerHeight * 0.85;
                const scaleX = availableWidth / originalDimensions.current.width;
                const scaleY = availableHeight / originalDimensions.current.height;
                const scale = Math.min(scaleX, scaleY);

                const newWidth = originalDimensions.current.width * scale;
                const newHeight = originalDimensions.current.height * scale;

                setDrawerStyles(prev => ({
                    ...prev,
                    width: newWidth,
                    height: newHeight
                }));
            }, 50);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPageIndex, loadCanvasForPage, isZoomed]);

    useEffect(() => {
        if (canvas.current && drawerStyles.height) {
            const canvasObjects = canvas.current.getObjects().length;
            const hasData = !!canvasDataPerPage.current[currentPageIndex];

            if (canvasObjects === 0 && hasData) {
                loadCanvasForPage();
            }
        }
    }, [canvas, drawerStyles.height, currentPageIndex, loadCanvasForPage]);

    const prevTreatmentTypeRef = useRef(treatmentType);

    useEffect(() => {
        if (prevTreatmentTypeRef.current && prevTreatmentTypeRef.current !== treatmentType) {
            canvasDataPerPage.current = {};
            canvasImagePerPage.current = {};
            formRef.current.canvasDataPerPage = {};
            formRef.current.canvasImagePerPage = {};
            formRef.current.canvasData = '';
            formRef.current.canvasImage = '';

            if (canvas.current) {
                canvas.current.clear();
                canvas.current.renderAll();
            }

            setCurrentPageIndex(0);
        }
        prevTreatmentTypeRef.current = treatmentType;
    }, [treatmentType, currentPageIndex]); // eslint-disable-line

    const prevPhotoRef = useRef(photo);
    useEffect(() => {
        const totalPages = hasOtherPages ? photo.otherPagesUrl.length + 1 : 1;

        if (prevPhotoRef.current?.url !== photo?.url) {
            // Clear internal canvas refs (form state is reset by parent onChange)
            canvasDataPerPage.current = {};
            canvasImagePerPage.current = {};

            // Clear fabric canvas instance
            if (canvas.current) {
                canvas.current.clear();
                if (canvas.current.historyUndo) canvas.current.historyUndo = [];
                if (canvas.current.historyRedo) canvas.current.historyRedo = [];
                canvas.current.historyNextState = canvas.current._historyNext();
                canvas.current.renderAll();
            }

            if (currentPageIndex >= totalPages) {
                setCurrentPageIndex(0);
            }
        }

        prevPhotoRef.current = photo;
    }, [photo, hasOtherPages, currentPageIndex]); // eslint-disable-line

    useEffect(() => {
        if (photo && photo.url !== 'None') {
            const urlsToPreload = [photo.url];
            if (hasOtherPages) {
                urlsToPreload.push(...photo.otherPagesUrl);
            }

            urlsToPreload.forEach(url => {
                if (!preloadedImages.current[url]) {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.src = url;
                    img.onload = () => {
                        preloadedImages.current[url] = img;
                        formRef.current.preloadedImages = preloadedImages.current;
                    };
                    img.onerror = () => {
                        const retry = new Image();
                        retry.crossOrigin = 'anonymous';
                        retry.src = url + (url.includes('?') ? '&' : '?') + '_cb=' + Date.now();
                        retry.onload = () => {
                            preloadedImages.current[url] = retry;
                            formRef.current.preloadedImages = preloadedImages.current;
                        };
                    };
                }
            });
            formRef.current.preloadedImages = preloadedImages.current;
        }
    }, [photo, hasOtherPages]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (photo && photo.url !== 'None') {
            const currentUrl =
                hasOtherPages && currentPageIndex > 0 ? photo.otherPagesUrl[currentPageIndex - 1] : photo.url;

            const applyImage = img => {
                const width = TREATMENT_PHOTO_WIDTH;
                const scale = width / img.width;
                const height = img.naturalHeight * scale;

                canvasSizeProps.current = { height, width };
                originalDimensions.current = { width, height };

                if (isZoomed) {
                    const availableWidth = window.innerWidth * 0.95;
                    const availableHeight = window.innerHeight * 0.85;
                    const scaleX = availableWidth / width;
                    const scaleY = availableHeight / height;
                    const zoomScale = Math.min(scaleX, scaleY);

                    canvas.current.setDimensions({
                        height: height * zoomScale,
                        width: width * zoomScale
                    });
                    canvas.current.setZoom(zoomScale);
                    setDrawerStyles({
                        height: height * zoomScale,
                        width: width * zoomScale
                    });
                } else {
                    canvas.current.setDimensions({
                        height,
                        width
                    });
                    setDrawerStyles({
                        height,
                        width
                    });
                }
                setBackgroundUrl(currentUrl);
            };

            if (preloadedImages.current[currentUrl]) {
                applyImage(preloadedImages.current[currentUrl]);
            } else {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.src = currentUrl;
                img.onload = () => {
                    preloadedImages.current[currentUrl] = img;
                    applyImage(img);
                };
                img.onerror = () => {
                    const retry = new Image();
                    retry.crossOrigin = 'anonymous';
                    retry.src = currentUrl + (currentUrl.includes('?') ? '&' : '?') + '_cb=' + Date.now();
                    retry.onload = () => {
                        preloadedImages.current[currentUrl] = retry;
                        applyImage(retry);
                    };
                };
            }
        } else {
            setDrawerStyles({
                height: drawerStyles.height,
                width: drawerStyles.width
            });
            setBackgroundUrl(null);
        }
        //eslint-disable-next-line
    }, [photo, currentPageIndex]);

    useEffect(() => {
        const originalSave = () => {
            if (canvas.current) {
                const currentData = JSON.stringify(canvas.current.toJSON());
                const currentImage = exportCanvasToDataURL(canvas.current);

                canvasDataPerPage.current[currentPageIndex] = currentData;
                canvasImagePerPage.current[currentPageIndex] = currentImage;

                const totalPages = hasOtherPages ? photo.otherPagesUrl.length + 1 : 1;
                const currentPageKeys = Object.keys(canvasDataPerPage.current).map(Number);
                const pagesToRemove = currentPageKeys.filter(pageIndex => pageIndex >= totalPages);

                pagesToRemove.forEach(pageIndex => {
                    delete canvasDataPerPage.current[pageIndex];
                    delete canvasImagePerPage.current[pageIndex];
                });

                formRef.current.canvasDataPerPage = { ...canvasDataPerPage.current };
                formRef.current.canvasImagePerPage = { ...canvasImagePerPage.current };

                formRef.current.canvasData = currentData;
                formRef.current.canvasImage = currentImage;

                if (updateForm) updateForm();
            }
        };

        formRef.current.saveCanvasForCurrentPage = originalSave;
    }, [canvas, currentPageIndex, formRef, updateForm, hasOtherPages, photo]);

    const dropZone = useDropUnit(canvas, canvasRef, formRef, updateForm, dragSnapShot);

    useLoadCanvas(canvas, canvasRef, formRef, updateForm);
    useLineAndArrow(canvas, formRef);

    const UnitPreview = ({ item, style }) => {
        return (
            <div style={{ ...style, zIndex: 10000 }}>
                <UnitButtonLayer
                    item={item}
                    style={style}
                    dragSnapShot={dragSnapShot}
                    shape={shape}
                    borderColour={borderColour}
                />
            </div>
        );
    };

    UnitPreview.propTypes = {
        item: PropTypes.object.isRequired,
        style: PropTypes.object.isRequired
    };

    const toggleZoom = () => {
        if (!canvas.current) return;

        if (isZoomed) {
            canvas.current.setWidth(originalDimensions.current.width);
            canvas.current.setHeight(originalDimensions.current.height);
            canvas.current.setZoom(1);

            setDrawerStyles(prev => ({
                ...prev,
                width: originalDimensions.current.width,
                height: originalDimensions.current.height
            }));

            setIsZoomed(false);
        } else {
            const availableWidth = window.innerWidth * 0.95;
            const availableHeight = window.innerHeight * 0.85;

            const scaleX = availableWidth / originalDimensions.current.width;
            const scaleY = availableHeight / originalDimensions.current.height;
            const scale = Math.min(scaleX, scaleY);

            const newWidth = originalDimensions.current.width * scale;
            const newHeight = originalDimensions.current.height * scale;

            canvas.current.setZoom(scale);
            canvas.current.setWidth(newWidth);
            canvas.current.setHeight(newHeight);

            setDrawerStyles(prev => ({
                ...prev,
                width: newWidth,
                height: newHeight
            }));

            setIsZoomed(true);
        }
        canvas.current.renderAll();
    };

    const handlePrev = () => {
        if (currentPageIndex > 0) {
            if (canvas.current) {
                const currentData = JSON.stringify(canvas.current.toJSON());
                const currentImage = exportCanvasToDataURL(canvas.current);
                canvasDataPerPage.current[currentPageIndex] = currentData;
                canvasImagePerPage.current[currentPageIndex] = currentImage;

                const totalPages = hasOtherPages ? photo.otherPagesUrl.length + 1 : 1;
                const currentPageKeys = Object.keys(canvasDataPerPage.current).map(Number);
                const pagesToRemove = currentPageKeys.filter(pageIndex => pageIndex >= totalPages);

                pagesToRemove.forEach(pageIndex => {
                    delete canvasDataPerPage.current[pageIndex];
                    delete canvasImagePerPage.current[pageIndex];
                });

                formRef.current.canvasDataPerPage = { ...canvasDataPerPage.current };
                formRef.current.canvasImagePerPage = { ...canvasImagePerPage.current };
                formRef.current.canvasData = currentData;
                formRef.current.canvasImage = currentImage;
            }
            setCurrentPageIndex(currentPageIndex - 1);
        }
    };

    const handleNext = () => {
        const totalPages = hasOtherPages ? photo.otherPagesUrl.length + 1 : 1;
        if (currentPageIndex < totalPages - 1) {
            if (canvas.current) {
                const currentData = JSON.stringify(canvas.current.toJSON());
                const currentImage = exportCanvasToDataURL(canvas.current);
                canvasDataPerPage.current[currentPageIndex] = currentData;
                canvasImagePerPage.current[currentPageIndex] = currentImage;

                const currentPageKeys = Object.keys(canvasDataPerPage.current).map(Number);
                const pagesToRemove = currentPageKeys.filter(pageIndex => pageIndex >= totalPages);

                pagesToRemove.forEach(pageIndex => {
                    delete canvasDataPerPage.current[pageIndex];
                    delete canvasImagePerPage.current[pageIndex];
                });

                formRef.current.canvasDataPerPage = { ...canvasDataPerPage.current };
                formRef.current.canvasImagePerPage = { ...canvasImagePerPage.current };
                formRef.current.canvasData = currentData;
                formRef.current.canvasImage = currentImage;
            }
            setCurrentPageIndex(currentPageIndex + 1);
        }
    };

    const totalPages = hasOtherPages ? photo.otherPagesUrl.length + 1 : 1;

    return (
        <>
            {isZoomed && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        zIndex: 10000,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        overflow: 'visible'
                    }}
                >
                    {hasOtherPages && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '20px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 10000,
                                display: 'flex',
                                gap: '10px',
                                alignItems: 'center'
                            }}
                        >
                            <IconButton
                                onClick={handlePrev}
                                disabled={currentPageIndex === 0}
                                style={{
                                    backgroundColor: 'white',
                                    color: 'black',
                                    border: '1px solid #ddd',
                                    padding: '8px'
                                }}
                                size="small"
                                title="Previous page"
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <span className={classes.canvasPageIndicator} style={{ color: 'white', fontSize: '18px' }}>
                                {currentPageIndex + 1} / {totalPages}
                            </span>
                            <IconButton
                                onClick={handleNext}
                                disabled={currentPageIndex === totalPages - 1}
                                style={{
                                    backgroundColor: 'white',
                                    color: 'black',
                                    border: '1px solid #ddd',
                                    padding: '8px'
                                }}
                                size="small"
                                title="Next page"
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </div>
                    )}
                    <div
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            zIndex: 10000
                        }}
                    >
                        <IconButton
                            onClick={toggleZoom}
                            title="Close fullscreen"
                            style={{
                                backgroundColor: 'white',
                                color: 'black',
                                border: '1px solid #ddd',
                                padding: '8px'
                            }}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>
            )}

            {isZoomed && (
                <Box
                    style={{
                        position: 'fixed',
                        bottom: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10002,
                        backgroundColor: 'white',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        gap: '4px'
                    }}
                    display="flex"
                >
                    <Tools canvas={canvas} formRef={formRef} />
                    <Actions canvas={canvas} updateForm={updateForm} formRef={formRef} />
                </Box>
            )}

            <div className={classes.treatmentBlock} id="treatment-canvas" ref={dropZone}>
                <div
                    style={{
                        ...drawerStyles,
                        position: 'relative',
                        ...(isZoomed
                            ? {
                                  position: 'fixed',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  zIndex: 10001
                              }
                            : {}),
                        pointerEvents: formRef.current.lockEdit || (isDrawerLocked && !isDragging) ? 'none' : 'auto'
                    }}
                >
                    <img
                        crossOrigin="anonymous"
                        src={backgroundUrl || ''}
                        alt=""
                        onError={e => {
                            const src = e.target.src;
                            if (src && !src.includes('_cb=')) {
                                e.target.src = src + (src.includes('?') ? '&' : '?') + '_cb=' + Date.now();
                            }
                        }}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                            display: backgroundUrl ? 'block' : 'none'
                        }}
                    />
                    <canvas
                        id="fabric-canvas"
                        height={drawerStyles.height?.toString() || '300'}
                        width={drawerStyles.width?.toString() || TREATMENT_PHOTO_WIDTH}
                        ref={canvasRef}
                    />
                    &nbsp;
                </div>

                {!isZoomed && (
                    <div
                        className={classes.canvasNav}
                        style={{ marginTop: '16px', ...(!hasOtherPages && { justifyContent: 'center' }) }}
                    >
                        {hasOtherPages && (
                            <>
                                <IconButton
                                    onClick={handlePrev}
                                    disabled={currentPageIndex === 0}
                                    style={{
                                        backgroundColor: 'white',
                                        color: 'black',
                                        border: '1px solid #ddd',
                                        padding: '8px'
                                    }}
                                    size="small"
                                    title="Previous page"
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                                <span className={classes.canvasPageIndicator}>
                                    {currentPageIndex + 1} / {totalPages}
                                </span>
                                <IconButton
                                    onClick={handleNext}
                                    disabled={currentPageIndex === totalPages - 1}
                                    style={{
                                        backgroundColor: 'white',
                                        color: 'black',
                                        border: '1px solid #ddd',
                                        padding: '8px'
                                    }}
                                    size="small"
                                    title="Next page"
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </>
                        )}
                        <IconButton
                            onClick={toggleZoom}
                            title="Open fullscreen"
                            style={{
                                marginLeft: hasOtherPages ? '20px' : undefined,
                                backgroundColor: 'white',
                                color: 'black',
                                border: '1px solid #ddd',
                                padding: '8px'
                            }}
                            size="small"
                        >
                            <ZoomInIcon />
                        </IconButton>
                    </div>
                )}

                {!isZoomed && (
                    <Box my={2} display="flex" justifyContent="space-between">
                        <Tools canvas={canvas} formRef={formRef} />
                        <Actions canvas={canvas} updateForm={updateForm} formRef={formRef} />
                    </Box>
                )}

                <Preview generator={UnitPreview} />
            </div>
        </>
    );
}

Canvas.propTypes = {
    classes: PropTypes.object.isRequired,
    formRef: PropTypes.object.isRequired,
    updateForm: PropTypes.func.isRequired,
    canvas: PropTypes.object.isRequired,
    photo: PropTypes.object,
    canvasSizeProps: PropTypes.object.isRequired,
    dragSnapShot: PropTypes.object.isRequired,
    shape: PropTypes.string,
    borderColour: PropTypes.string,
    treatmentType: PropTypes.string
};

export default withStyles(styles)(Canvas);
